import React, {useRef, forwardRef, useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import Slider from 'react-slick'

import {
    AspectRatio,
    Box,
    Heading,
    Skeleton,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useProducts} from '@salesforce/commerce-sdk-react'
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer'
import {useThinkAnalyticsProductRecommendations} from '../../hooks/use-think-analytics-product-recommendations'
import useSegment from '../../integrations/segment/app/hooks/use-segment'
import useCustomShopperContext from '../../hooks/use-custom-shopper-context.js'
import {useWishList} from '../../hooks/use-wish-list'
import {useCurrentCustomer} from '../../hooks/use-current-customer.js'
import {useCustomerGroups} from '../../hooks/use-customer-groups'
import {useWishlistActions} from '../../hooks/use-wishlist-action.js'

import ProductTile from '../../../app/components/product-tile'
import {BlackArrow} from '../../components/icons'
import ProductTileRecentlyViewed from '../../components/product-carousel/product-tile-recently-viewed'
import {getBreadcrumbIds, isCustomerInProductGroups} from '../../utils/custom-utils.js'
import {getActiveSlideProducts} from '../../integrations/segment/app/utils/segment-utils'

const TAContentSlot = forwardRef(({title, contentSlot, enableFavourite, ...props}, ref) => {
    const scrollRef = useRef()
    const visibleRef = useRef()
    const {data: wishlist} = useWishList()
    const {addItemToWishlist, removeItemFromWishlist} = useWishlistActions()
    const {data: customer} = useCurrentCustomer()
    const {data: customerGroups} = useCustomerGroups(customer?.isGuest)
    const customerGroupList = customer?.c_customerGroups || customerGroups?.result
    const segment = useSegment()
    const [activeSlideProducts, setActiveSlideProducts] = useState([])
    const isRecommenderOnScreen = useIntersectionObserver(visibleRef, {useOnce: true})

    let slotData = contentSlot?.jsx || {}
    title = slotData?.calloutMsg || title

    if (typeof window !== 'undefined') {
        const breadcrumbs = getBreadcrumbIds()

        if (slotData.filterOne && !slotData.filterOneValue) {
            slotData.filterOneValue = breadcrumbs.length > 1 ? breadcrumbs[1] : ''
        }

        if (slotData.filterTwo && !slotData.filterTwoValue) {
            slotData.filterTwoValue =
                breadcrumbs.length > 2 ? breadcrumbs[breadcrumbs.length - 1] : ''
        }
    }

    const useCase = slotData?.useCase?.trim()
        ? `Store%2FRecs%2F${slotData.useCase.trim()}`
        : 'Store%2FRecs%2FRecsForYou'

    const shopperGeoLocation = useCustomShopperContext()

    const {data: productData = [], isLoading: isRecsLoading} =
        useThinkAnalyticsProductRecommendations(slotData, shopperGeoLocation, {
            enabled: !!shopperGeoLocation
        })
    const validProductIds = Array.isArray(productData) ? productData : []
    const {data: productsResponse, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: validProductIds.join(','),
                allImages: true,
                expand: ['availability', 'prices', 'images', 'variations', 'promotions', 'options']
            }
        },
        {
            enabled: validProductIds.length > 0
        }
    )

    // filter orderable and customer groups products
    const recommendations = (productsResponse?.data ?? []).filter((product) => {
        const productCustomerGroups = product?.c_customerGroups || []
        const isAllowed = isCustomerInProductGroups(productCustomerGroups, customerGroupList)

        return product?.inventory?.orderable === true && isAllowed
    })

    const handleSlideChange = (currentSlide) => {
        const activeProducts = getActiveSlideProducts(currentSlide, recommendations, scrollRef, '')
        setActiveSlideProducts(activeProducts)
    }

    useEffect(() => {
        if (isRecommenderOnScreen && recommendations) {
            if (activeSlideProducts?.activeProducts?.length > 0) {
                segment.sendProductListViewed(activeSlideProducts, '', '', visibleRef.current)
            }
        }
    }, [isRecommenderOnScreen, activeSlideProducts])

    useEffect(() => {
        if (isRecommenderOnScreen && recommendations) {
            const timer = setTimeout(() => {
                handleSlideChange()
            }, 100)
            return () => clearTimeout(timer)
        }
    }, [recommendations, isRecommenderOnScreen])

    const handleClick = (onClick) => {
        if (typeof onClick === 'function') {
            onClick()
        }
        setTimeout(handleSlideChange, 100)
    }

    const NextArrow = ({className, onClick}) => (
        <Box
            className={className}
            onClick={() => handleClick(onClick)}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => {
                if (e.key === 'Enter') handleClick(onClick)
            }}
            position="absolute"
            right={'0'}
        >
            <BlackArrow />
        </Box>
    )

    const PrevArrow = ({className, onClick}) => (
        <Box
            className={className}
            onClick={() => handleClick(onClick)}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => {
                if (e.key === 'Enter') handleClick(onClick)
            }}
            transform="rotate(180deg)"
            position="absolute"
            right="105px"
        >
            <BlackArrow />
        </Box>
    )

    const sliderSettings = {
        infinite: false,
        speed: 500,
        slidesToShow: 5,
        slidesToScroll: 3,
        arrows: true,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        responsive: [
            {
                breakpoint: 1200,
                settings: {
                    slidesToShow: 4,
                    slidesToScroll: 2,
                    arrows: true
                }
            },
            {
                breakpoint: 767,
                settings: {
                    slidesToShow: 1.3,
                    slidesToScroll: 1,
                    arrows: false,
                    afterChange: (currentSlide) => {
                        setTimeout(() => handleSlideChange(currentSlide), 100)
                    }
                }
            }
        ]
    }

    if ((!isRecsLoading || isProductsLoading) && (!recommendations || recommendations.length === 0))
        return null

    return (
        <Box
            position="relative"
            ref={ref}
            data-testid="product-carousel"
            className="carousel-wrapper product-wrapper ta-wrapper"
        >
            <Box {...props} ref={visibleRef}>
                {title && !isRecsLoading && productData.length > 0 && (
                    <Heading as="h2" className="recommenderHeading">
                        {title}
                    </Heading>
                )}

                <Slider
                    {...sliderSettings}
                    ref={scrollRef}
                    className="carousel-sg product-carousel-slider"
                >
                    {isRecsLoading
                        ? [0, 1, 2, 3].map((key) => (
                              <Box key={key} flex="0 0 auto" width="275px" mx={2}>
                                  <Stack>
                                      <AspectRatio ratio={1}>
                                          <Skeleton />
                                      </AspectRatio>
                                      <Skeleton height="20px" width="80%" />
                                      <Skeleton height="20px" width="50%" />
                                  </Stack>
                              </Box>
                          ))
                        : recommendations?.map((product, index) => (
                              <Box
                                  key={product?.id || index}
                                  className={
                                      props?.isProductTileRecentlyViewed
                                          ? 'recently-viewed-block'
                                          : 'product-tile-block'
                                  }
                                  flex="0 0 auto"
                                  style={{scrollSnapAlign: 'start'}}
                              >
                                  {props?.isProductTileRecentlyViewed ? (
                                      <ProductTileRecentlyViewed
                                          product={product}
                                          aria-label={product?.name}
                                          productPosition={index}
                                          data-testid="product-scroller-item"
                                          dynamicImageProps={{
                                              widths: ['70vw', '70vw', '40vw', '30vw']
                                          }}
                                      />
                                  ) : (
                                      <ProductTile
                                          product={product}
                                          aria-label={product?.name}
                                          productPosition={index}
                                          data-testid="product-scroller-item"
                                          source={useCase}
                                          dynamicImageProps={{
                                              widths: ['70vw', '70vw', '40vw', '30vw']
                                          }}
                                          enableFavourite={enableFavourite}
                                          isFavourite={wishlist?.productWishList[0]?.customerProductListItems?.some(
                                              (item) => item.productId === product.id
                                          )}
                                          onFavouriteToggle={(
                                              isFavourite,
                                              productDetail,
                                              event
                                          ) => {
                                              const action = isFavourite
                                                  ? (p, event) =>
                                                        addItemToWishlist(
                                                            p,
                                                            index,
                                                            productDetail,
                                                            event,
                                                            useCase
                                                        )
                                                  : (p) =>
                                                        removeItemFromWishlist(
                                                            p,
                                                            index,
                                                            productDetail
                                                        )
                                              return action(product, event)
                                          }}
                                      />
                                  )}
                              </Box>
                          ))}
                </Slider>
            </Box>
        </Box>
    )
})

TAContentSlot.displayName = 'ProductCarousel'

TAContentSlot.propTypes = {
    title: PropTypes.any,
    isProductTileRecentlyViewed: PropTypes.bool,
    isPdProductTile: PropTypes.bool,
    contentSlot: PropTypes.object,
    className: PropTypes.string,
    onClick: PropTypes.func,
    enableFavourite: PropTypes.bool
}

export default TAContentSlot
